// Home → Reports (spec 044 D76–D89, plan slice 8): every read the page makes. Counts and sums
// are aggregation queries on indexes both projects already hold (D81), each carrying the
// orderBy its index is sorted by — without it Firestore asks for another index (slice 4a).
// Every shape here answered on staging and production by REST on 2026-09-29; step 2's two
// (the no-show reports, the summaries) are single-field and id ranges, which need no index.
import {
  collection,
  doc,
  documentId,
  getAggregateFromServer,
  getCountFromServer,
  getDoc,
  getDocs,
  limit as qlimit,
  orderBy,
  query,
  sum,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { NO_SHOW_REASONS } from "../utils/reportNumbers";
import { statsSpan } from "../utils/reportCharts";

const users = collection(db, "users");
const bookings = collection(db, "bookings");
const ledger = collection(db, "walletTransactions");
const stats = collection(db, "stats");

const between = (field, { from, to }) => [
  where(field, ">=", Timestamp.fromDate(from)),
  where(field, "<", Timestamp.fromDate(to)),
  orderBy(field, "desc"),
];
const rowsOf = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
// R4: a test-account read that reaches the cap would take out only part of the test data;
// it fails instead, so every number shows "—".
const uncapped = (snap) => {
  if (snap.size >= 1000) throw new Error("a test-account read reached 1,000 rows");
  return rowsOf(snap);
};

// R5: a read that fails is null in the answer and "—" on the page; the rest still lands.
const settle = (what, promise) =>
  promise.catch((e) => {
    console.error(`Reports: ${what} failed:`, e);
    return null;
  });
const count = (what, col, ...parts) =>
  settle(what, getCountFromServer(query(col, ...parts)).then((s) => s.data().count));
// Collected, as Transactions sums it — on (status, createdAt desc, amount desc), slice 7 R17.
export const collected = (what, window) =>
  settle(
    what,
    getAggregateFromServer(query(ledger, where("status", "==", "succeeded"), ...between("createdAt", window)), {
      total: sum("amount"),
    }).then((s) => s.data().total)
  );

// `in` takes at most 30 values.
const inThirties = (ids) => {
  const out = [];
  for (let i = 0; i < ids.length; i += 30) out.push(ids.slice(i, i + 30));
  return out;
};

// D71, R4: the test accounts, then their own lessons (as student or as tutor) and ledger rows
// over both windows, for reportNumbers to take out. Rejects if any part fails.
// ponytail: an unbounded test-account read (8 on production) and 1,000 rows per `in` read —
// reaching it fails the numbers rather than under-subtract — (47 lessons and 62 ledger rows
// over two years); past that, step 2's nightly rollup.
const readTestAccounts = async ({ whole, soFar }) => {
  const people = rowsOf(await getDocs(query(users, where("isInternal", "==", true))));
  const lessonSpan = { from: whole.prev.from, to: whole.to };
  const moneySpan = { from: soFar.prev.from, to: soFar.to };
  const lessons = new Map();
  const topups = [];
  await Promise.all(
    inThirties(people.map((p) => p.id)).flatMap((ids) => [
      getDocs(query(bookings, where("studentId", "in", ids), ...between("startTimeUTC", lessonSpan), qlimit(1000))).then(
        (s) => uncapped(s).forEach((b) => lessons.set(b.id, b))
      ),
      getDocs(query(bookings, where("teacherId", "in", ids), ...between("startTimeUTC", lessonSpan), qlimit(1000))).then(
        (s) => uncapped(s).forEach((b) => lessons.set(b.id, b))
      ),
      getDocs(query(ledger, where("userId", "in", ids), ...between("createdAt", moneySpan), qlimit(1000))).then((s) =>
        topups.push(...uncapped(s))
      ),
    ])
  );
  return { users: people, bookings: [...lessons.values()], topups };
};

// Money → Overview (D103): the test accounts and their own ledger rows in `span`, for the page
// to take out — the same reads and the same 1,000-row rule (R4) as readTestAccounts.
export const readTestLedger = async (span) => {
  const people = rowsOf(await getDocs(query(users, where("isInternal", "==", true))));
  const rows = [];
  await Promise.all(
    inThirties(people.map((p) => p.id)).map((ids) =>
      getDocs(query(ledger, where("userId", "in", ids), ...between("createdAt", span), qlimit(1000))).then((s) =>
        rows.push(...uncapped(s))
      )
    )
  );
  return { users: people, rows };
};

// D74, R3: the approved tutors, test accounts out, each with four counts — lessons and
// cancelled lessons, in the period and in the period before.
// ponytail: four counts a tutor (9 approved on production); past a few hundred, step 2's rollup.
const readTutors = async ({ whole }) => {
  const approved = rowsOf(
    await getDocs(query(users, where("role", "==", "teacher"), where("verifyStatus", "==", "approved")))
  ).filter((t) => t.isInternal !== true);
  const cancelled = where("status", "==", "Cancelled");
  return Promise.all(
    approved.map(async (t) => {
      const theirs = where("teacherId", "==", t.id);
      const [total, cancelledN, totalBefore, cancelledBefore] = await Promise.all([
        count("a tutor's lessons", bookings, theirs, ...between("startTimeUTC", whole)),
        count("a tutor's cancelled lessons", bookings, theirs, cancelled, ...between("startTimeUTC", whole)),
        count("a tutor's lessons before", bookings, theirs, ...between("startTimeUTC", whole.prev)),
        count("a tutor's cancelled lessons before", bookings, theirs, cancelled, ...between("startTimeUTC", whole.prev)),
      ]);
      return { id: t.id, cur: { total, cancelled: cancelledN }, prev: { total: totalBefore, cancelled: cancelledBefore } };
    })
  );
};

// D86: every lesson reported as a no-show — the report form's label opens `reportReason`, so a
// range on that one field from the label to the label followed by the highest character finds
// them on its automatic index. Rejects past 1,000 of either (R4: the numbers fail, not shrink).
// ponytail: all of them, ever (0 on production); a date bound needs a (reportReason,
// startTimeUTC) index — add it when the list grows past a few hundred.
const readNoShows = async () =>
  (
    await Promise.all(
      NO_SHOW_REASONS.map((label) =>
        getDocs(query(bookings, where("reportReason", ">=", label), where("reportReason", "<", `${label}\uf8ff`), qlimit(1000))).then(
          uncapped
        )
      )
    )
  ).flat();

// Everything the page shows for `windows` (reportPeriods.reportWindows), in the shape
// reportNumbers takes. Never rejects: a read that fails is null.
export const getReport = async (windows) => {
  const { whole, soFar, now } = windows;
  const status = (s) => where("status", "==", s);
  const student = where("role", "==", "student");
  const [internal, tutors, noShows, revenue, revenueBefore, signups, signupsBefore, ...lessons] = await Promise.all([
    settle("the test accounts", readTestAccounts(windows)),
    settle("the tutors", readTutors(windows)),
    settle("the no-show reports", readNoShows()),
    collected("revenue", soFar),
    collected("revenue before", soFar.prev),
    count("new students", users, student, ...between("createdAt", soFar)),
    count("new students before", users, student, ...between("createdAt", soFar.prev)),
    count("lessons", bookings, ...between("startTimeUTC", whole)),
    count("completed", bookings, status("Finished"), ...between("startTimeUTC", whole)),
    count("cancelled", bookings, status("Cancelled"), ...between("startTimeUTC", whole)),
    // R2: upcoming is Pending from now to the period's end.
    count("upcoming", bookings, status("Pending"), ...between("startTimeUTC", { from: now, to: whole.to })),
    count("lessons before", bookings, ...between("startTimeUTC", whole.prev)),
    count("completed before", bookings, status("Finished"), ...between("startTimeUTC", whole.prev)),
    count("cancelled before", bookings, status("Cancelled"), ...between("startTimeUTC", whole.prev)),
  ]);
  const [total, completed, cancelled, upcoming, totalBefore, completedBefore, cancelledBefore] = lessons;
  return {
    internal,
    tutors,
    noShows,
    revenue: { cur: revenue, prev: revenueBefore },
    newStudents: { cur: signups, prev: signupsBefore },
    sessions: {
      cur: { total, completed, cancelled, upcoming },
      prev: { total: totalBefore, completed: completedBefore, cancelled: cancelledBefore },
    },
  };
};

// D84, D85: the day summaries from the period's first day to today (an id range — the ids are
// the days), or over `span` ({ from, to } day keys) — Reports passes the subject chart's
// (subjectSpan), which holds the period's; and stats/audience, which is also the sign that
// buildReportStats has run, and stats/hours for Busiest hours (D95). Rejects if any read fails;
// the page shows the charts' own error then.
// ponytail: Reports reads 24 months of small day summaries a load (about 750); past that, a
// month rollup written by buildReportStats.
export const getReportStats = async (windows, span = statsSpan(windows)) => {
  const { from, to } = span;
  const [days, audience, hours] = await Promise.all([
    getDocs(query(stats, where(documentId(), ">=", from), where(documentId(), "<=", to))),
    getDoc(doc(stats, "audience")),
    getDoc(doc(stats, "hours")),
  ]);
  return {
    days: new Map(days.docs.map((d) => [d.id, d.data()])),
    audience: audience.exists() ? audience.data() : null,
    hours: hours.exists() ? hours.data() : null,
  };
};
